// src/components/magdrops/PackageSelection.tsx
import { ArrowRight, Award, CheckCircle, Clock } from "lucide-react";

interface Package {
  id: string;
  title: string;
  subtitle: string;
  price: string;
  timeline: string;
  description: string;
  features: string[];
  perfect_for: string;
  badge?: string;
}

interface PackageSelectionProps {
  onPackageSelect: (pkg: Package) => void;
  onBack: () => void;
}

export default function PackageSelection({
  onPackageSelect,
  onBack,
}: PackageSelectionProps) {
  const packages: Package[] = [
    {
      id: "quick-run",
      title: "The Quick Run",
      subtitle: "Mag in a bag in 4 weeks",
      price: "£2,000",
      timeline: "4 weeks",
      description:
        "Fast turnaround magazine production using desktop content and basic templates",
      features: [
        "Week 1: Ideation, strategy & plan",
        "Week 2: Content creation",
        "Week 3: Proofing",
        "Week 4: Printing",
        "Basic template - colour blocks",
        "8, 12, or 24 page options",
      ],
      perfect_for:
        "Quick market entry, testing concepts, time-sensitive campaigns",
      badge: "Most Popular",
    },
    {
      id: "editor-run",
      title: "The Editor Run",
      subtitle: "Bespoke UG Content",
      price: "£6,000",
      timeline: "6 weeks",
      description:
        "Full editorial service with bespoke content, user-generated material, and professional editing",
      features: [
        "Week 1: Ideation, strategy & plan",
        "Week 2: Content creation",
        "Week 3: Editorial refinement",
        "Week 4: Professional proofing",
        "Week 5: Premium printing",
        "Week 6: Distribution support",
        "Bespoke design & UG content",
        "8, 12, or 24 page options",
      ],
      perfect_for: "Brand building, community engagement, premium positioning",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-900">
            Choose your package
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Both packages include strategy, content creation, and professional
            printing. The difference is in timeline and editorial depth.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {packages.map((pkg) => {
            const isQuickRun = pkg.id === "quick-run";
            const cardClasses = isQuickRun
              ? "bg-gradient-to-br from-blue-50 via-white to-blue-50 border-blue-200/50 hover:from-blue-100 hover:to-blue-50 hover:border-blue-300"
              : "bg-gradient-to-br from-purple-50 via-white to-pink-50 border-purple-200/50 hover:from-purple-100 hover:to-pink-50 hover:border-purple-300";

            return (
              <div
                key={pkg.id}
                className="group cursor-pointer transform transition-all duration-200 hover:-translate-y-1"
                onClick={() => onPackageSelect(pkg)}
              >
                <div
                  className={`rounded-2xl shadow-sm border transition-all h-full overflow-hidden ${cardClasses}`}
                >
                  {/* Feature Header Image */}
                  <div className="h-80 overflow-hidden bg-slate-100">
                    <img
                      src={
                        isQuickRun
                          ? "/src/assets/images/magdrops/quick-run.jpg"
                          : "/src/assets/images/magdrops/editor-run.jpg"
                      }
                      alt={pkg.title}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <div className="p-8">
                    {pkg.badge && (
                      <div
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold mb-6 ${
                          isQuickRun
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700"
                        }`}
                      >
                        <Award className="w-3 h-3 mr-1" />
                        {pkg.badge}
                      </div>
                    )}

                    <h3 className="text-2xl font-bold mb-2 text-slate-900">
                      {pkg.title}
                    </h3>
                    <p className="text-slate-500 text-sm mb-6">
                      {pkg.subtitle}
                    </p>

                    <div className="mb-6">
                      <span className="text-3xl font-bold text-slate-900">
                        {pkg.price}
                      </span>
                      <span className="text-slate-500 ml-2">+ print costs</span>
                    </div>

                    <div className="flex items-center mb-6 text-sm text-blue-600">
                      <Clock className="w-4 h-4 mr-2" />
                      <span className="font-medium">{pkg.timeline}</span>
                    </div>

                    <p className="text-slate-600 mb-6 leading-relaxed">
                      {pkg.description}
                    </p>

                    <div className="space-y-3 mb-6">
                      {pkg.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-600">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4 mb-6">
                      <div className="text-xs text-slate-500 mb-2">
                        Perfect for:
                      </div>
                      <div className="text-sm text-slate-700">
                        {pkg.perfect_for}
                      </div>
                    </div>

                    <div className="flex items-center text-blue-600 font-medium group-hover:translate-x-1 transition-transform">
                      <span>Select this package</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={onBack}
            className="text-slate-500 hover:text-slate-700 transition-colors"
          >
            ← Back to overview
          </button>
        </div>
      </div>
    </div>
  );
}
